import { apiSlice } from "../store/app.api";
import { supabase } from "../utils/API";

const mallAPi = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    getMallsA: build.query<
      { list: any[]; count: number },
      {
        page: number;
        perPage: number;
        searchTerm?: string;
        startDate?: string | null;
        endDate?: string | null;
        sortBy?: string;
        sortDir?: "asc" | "desc";
      }
    >({
      queryFn: async ({
        page,
        perPage,
        searchTerm,
        startDate,
        endDate,
        sortBy,
        sortDir,
      }) => {
        const { data, error } = await supabase.rpc(
          "get_theatre_groups_with_movie_report_count",
          {
            p_search: searchTerm || null,
            p_page: page,
            p_page_size: perPage,
            p_start_date: startDate,
            p_end_date: endDate,
            p_sort_by: sortBy || "name",
            p_sort_dir: sortDir || "asc",
          }
        );

        if (error) return { error };

        const list =
          data?.map((item: any) => ({
            id: item.id,
            code_pass: item.code_pass,
            name: item.name,
            street_address: item.street_address,
            barangay_address: item.barangay_address,
            city_address: item.city_address,
            province_address: item.province_address,
            logo_url: item.logo_url,
            is_app_displayed: item.is_app_displayed === true,
            report_count: Number(item.movie_reports_count ?? 0),
          })) ?? [];

        return {
          data: {
            list,
            count: Number(data?.[0]?.total_rows ?? 0),
          },
        };
      },
      providesTags: ["mall"],
    }),

    getMalls: build.query<any, any>({
      queryFn: async () => {
        const { data, error } = await supabase
          .from("theatre_groups")
          .select("*")
          .order("name", { ascending: true });

        if (error) return { error };
        return { data };
      },
      providesTags: ["mall"],
    }),

    getMallById: build.query<any, string>({
      queryFn: async (id) => {
        const { data, error } = await supabase
          .from("theatre_groups")
          .select("*")
          .eq("id", id)
          .single();

        if (error) return { error };
        return { data };
      },
      providesTags: ["mall"],
    }),

    getMallCinemas: build.query<any, any>({
      queryFn: async (ids: any) => {
        const { data, error } = await supabase.from("theaters").select("*");
        if (error) return { error };
        return { data };
      },
      providesTags: ["mall"],
    }),

    deleteMall: build.mutation<any, any>({
      queryFn: async (id) => {
        const { data, error } = await supabase
          .from("theatre_groups")
          .delete()
          .eq("id", id);

        if (error) return { error };
        return { data };
      },
      invalidatesTags: ["mall"],
    }),

    addMall: build.mutation<any, any>({
      queryFn: async (mall: any) => {
        const { data, error } = await supabase
          .from("theatre_groups")
          .insert(mall)
          .select()
          .single();

        if (error) return { error };
        return { data };
      },
      invalidatesTags: ["mall"],
    }),

    updateMall: build.mutation<any, any>({
      queryFn: async (mall: any) => {
        const { id, ...rest } = mall;

        const { data, error } = await supabase
          .from("theatre_groups")
          .update(rest)
          .eq("id", id)
          .select()
          .single();

        if (error) return { error };
        return { data };
      },
      invalidatesTags: ["mall"],
    }),

    getMallTicketUrls: build.query<any, string>({
      queryFn: async (mall_id) => {
        const { data, error } = await supabase
          .from("theatre_group_ticket_urls")
          .select("*")
          .eq("theatre_group_id", mall_id)
          .order("created_at", { ascending: false });

        if (error) return { error };
        return { data };
      },
      providesTags: ["mall"],
    }),

    addMallTicketUrl: build.mutation<any, any>({
      queryFn: async (urlData) => {
        const { data, error } = await supabase
          .from("theatre_group_ticket_urls")
          .insert({
            url: urlData.url,
            theatre_group_id: urlData.theatre_group_id,
          })
          .select()
          .single();

        if (error) return { error };
        return { data };
      },
      invalidatesTags: ["mall"],
    }),

    deleteMallTicketUrl: build.mutation<any, string>({
      queryFn: async (id) => {
        const { error } = await supabase
          .from("theatre_group_ticket_urls")
          .delete()
          .eq("id", id);

        if (error) return { error };
        return { data: null };
      },
      invalidatesTags: ["mall"],
    }),

    noReportMalls: build.query<any, any>({
      queryFn: async ({ startDate, endDate }) => {
        const { data, error } = await supabase.rpc("malls_without_reports", {
          start_date: startDate,
          end_date: endDate,
        });

        if (error) return { error };
        return { data };
      },
      providesTags: ["mall"],
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetMallCinemasQuery,
  useGetMallsQuery,
  useGetMallsAQuery,
  useLazyGetMallsAQuery,
  useGetMallByIdQuery,
  useLazyGetMallCinemasQuery,
  useAddMallMutation,
  useDeleteMallMutation,
  useUpdateMallMutation,
  useGetMallTicketUrlsQuery,
  useAddMallTicketUrlMutation,
  useDeleteMallTicketUrlMutation,
  useNoReportMallsQuery,
} = mallAPi;